export  const processImageWithOrientation = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        
        // Get EXIF orientation
        getImageOrientation(file).then((orientation) => {
          const w = img.width;
          const h = img.height;

          // Heuristic: if browser already applied EXIF and decoded image is portrait while orientation says rotate, skip
          if (orientation >= 5 && orientation <= 8 && h > w) {
            orientation = 1;
          }

          // Set canvas dimensions based on orientation
          if (orientation >= 5 && orientation <= 8) {
            canvas.width = h;
            canvas.height = w;
          } else {
            canvas.width = w;
            canvas.height = h;
          }

          // Fill background to avoid black when converting to JPEG (no alpha)
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Apply transformations based on EXIF orientation using translate/rotate/scale
          switch (orientation) {
            case 2: // horizontal flip
              ctx.translate(w, 0);
              ctx.scale(-1, 1);
              break;
            case 3: // 180°
              ctx.translate(w, h);
              ctx.rotate(Math.PI);
              break;
            case 4: // vertical flip
              ctx.translate(0, h);
              ctx.scale(1, -1);
              break;
            case 5: // transpose
              ctx.rotate(0.5 * Math.PI);
              ctx.scale(1, -1);
              break;
            case 6: // rotate 90° CW
              ctx.rotate(0.5 * Math.PI);
              ctx.translate(0, -h);
              break;
            case 7: // transverse
              ctx.rotate(0.5 * Math.PI);
              ctx.translate(w, -h);
              ctx.scale(-1, 1);
              break;
            case 8: // rotate 270° CW
              ctx.rotate(-0.5 * Math.PI);
              ctx.translate(-w, 0);
              break;
            default:
              // No transformation needed
              break;
          }

          // Draw the image
          ctx.drawImage(img, 0, 0);

          // Convert to data URL with good quality
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          resolve(dataUrl);
        }).catch(reject);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// EXIF orientation detection function
const getImageOrientation = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const view = new DataView(e.target?.result as ArrayBuffer);

        // Must be a JPEG
        if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) {
          resolve(1);
          return;
        }

        const length = view.byteLength;
        let offset = 2;

        while (offset + 4 <= length) {
          const marker = view.getUint16(offset, false);
          offset += 2;

          if (marker === 0xFFE1) { // APP1 (EXIF)
            if (offset + 2 > length) { resolve(1); return; }
            const exifLen = view.getUint16(offset, false);
            offset += 2;
            const exifStart = offset; // start of 'Exif\0\0'
            const exifEnd = exifStart + exifLen - 2; // total bytes after length field

            if (exifStart + 6 > length) { resolve(1); return; }
            if (view.getUint32(exifStart, false) !== 0x45786966) { resolve(1); return; } // 'Exif'
            const tiffStart = exifStart + 6; // TIFF header starts here
            if (tiffStart + 8 > length) { resolve(1); return; }

            const little = view.getUint16(tiffStart, false) === 0x4949; // 'II' (little) else big
            if (view.getUint16(tiffStart + 2, little) !== 0x002A) { resolve(1); return; } // TIFF magic
            const ifd0Offset = view.getUint32(tiffStart + 4, little);
            let ifd0 = tiffStart + ifd0Offset;
            if (ifd0 < tiffStart || ifd0 + 2 > length || ifd0 > exifEnd) { resolve(1); return; }
            const tagCount = view.getUint16(ifd0, little);
            ifd0 += 2;

            for (let i = 0; i < tagCount; i++) {
              const entryOffset = ifd0 + i * 12;
              if (entryOffset + 12 > length || entryOffset > exifEnd) { break; }
              const tag = view.getUint16(entryOffset, little);
              if (tag === 0x0112) {
                const orientation = view.getUint16(entryOffset + 8, little);
                resolve(orientation);
                return;
              }
            }
            resolve(1);
            return;
          } else if ((marker & 0xFF00) !== 0xFF00) {
            break; // invalid marker
          } else {
            // Skip this segment
            if (offset + 2 > length) { break; }
            const size = view.getUint16(offset, false);
            offset += size;
          }
        }
        resolve(1); // Default orientation
      } catch {
        // Any parsing error: assume normal orientation
        resolve(1);
      }
    };

    reader.readAsArrayBuffer(file);
  });
};

// New: Process image and return a JPEG Blob (mobile-friendly, avoids data: URL fetch)
export const processImageToJpegBlob = (file: File, options?: { quality?: number; maxDimension?: number; background?: string; }): Promise<Blob> => {
  const quality = options?.quality ?? 0.9;
  const maxDimension = options?.maxDimension ?? 1920; // downscale huge camera images
  const background = options?.background ?? '#ffffff';

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.onload = async () => {
          const origW = img.width;
          const origH = img.height;

          // Compute scale to limit max dimension
          const scale = Math.min(1, maxDimension / Math.max(origW, origH));
          const targetW = Math.round(origW * scale);
          const targetH = Math.round(origH * scale);

          let orientation = await getImageOrientation(file).catch(() => 1);
          // Heuristic: if browser already applied EXIF (common on iOS), decoded image will be portrait for 90° rotations
          if (orientation >= 5 && orientation <= 8 && origH > origW) {
            orientation = 1; // skip rotation to avoid double-rotating
          }

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          // Set canvas dimensions based on orientation
          if (orientation >= 5 && orientation <= 8) {
            canvas.width = targetH;
            canvas.height = targetW;
          } else {
            canvas.width = targetW;
            canvas.height = targetH;
          }

          // Fill background (JPEG has no alpha)
          ctx.fillStyle = background;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Apply EXIF transforms using scaled target sizes
          switch (orientation) {
            case 2: // horizontal flip
              ctx.translate(targetW, 0); ctx.scale(-1, 1); break;
            case 3: // 180°
              ctx.translate(targetW, targetH); ctx.rotate(Math.PI); break;
            case 4: // vertical flip
              ctx.translate(0, targetH); ctx.scale(1, -1); break;
            case 5: // transpose (mirror horizontal and rotate 270 CW)
              ctx.rotate(0.5 * Math.PI); ctx.scale(1, -1); break;
            case 6: // rotate 90° CW
              ctx.rotate(0.5 * Math.PI); ctx.translate(0, -targetH); break;
            case 7: // transverse (mirror horizontal and rotate 90 CW)
              ctx.rotate(0.5 * Math.PI); ctx.translate(targetW, -targetH); ctx.scale(-1, 1); break;
            case 8: // rotate 270° CW
              ctx.rotate(-0.5 * Math.PI); ctx.translate(-targetW, 0); break;
            default:
              break;
          }

          // Draw scaled image into oriented canvas
          ctx.drawImage(img, 0, 0, origW, origH, 0, 0, targetW, targetH);

          // Prefer toBlob over dataURL fetch for mobile reliability
          if (canvas.toBlob) {
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else {
                try {
                  const fallback = dataURLtoBlob(canvas.toDataURL('image/jpeg', quality));
                  resolve(fallback);
                } catch (err) {
                  reject(err);
                }
              }
            }, 'image/jpeg', quality);
          } else {
            try {
              const fallback = dataURLtoBlob(canvas.toDataURL('image/jpeg', quality));
              resolve(fallback);
            } catch (err) {
              reject(err);
            }
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

function dataURLtoBlob(dataurl: string): Blob {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}